import type { ImMNElement, MNDrawingContent } from '../../../../../../common/elements/elementModelTypes';
import { getDrawingData } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { getHeight, getWidth, getX, getY } from '../../../../../../common/maths/geometry/rect';
import { asObject } from '../../../../../../common/utils/immutableHelper';
import { getViewBox } from '../../../../../utils/svgUtils';
import { getImageDrawingCropRect } from '../../../imageDrawingUtils';
import type { DrawingTransform } from '../../utils/imageDrawingTransformUtils';
import { flipImageCropRect, rotateImageCropRect } from './imageModalEditorDataUtils';
import type { ImageCropRect, ImageSize } from './imageModalTransformTypes';

/** Remaps a drawing-space crop through the same session transform baked into its SVG paths. */
const remapDrawingCrop = (
    crop: ImageCropRect,
    canvas: ImageSize,
    sessionTransform: DrawingTransform,
): ImageCropRect => {
    const scaleX = sessionTransform.scaleX === -1 ? -1 : 1;
    const scaleY = sessionTransform.scaleY === -1 ? -1 : 1;
    const flippedCrop = flipImageCropRect(crop, canvas, scaleX, scaleY);
    return rotateImageCropRect(flippedCrop, canvas, sessionTransform.rotation);
};

/** Returns the drawing viewBox matching the full source or currently displayed edited image. */
export const getImageModalEditorDrawingViewBox = (
    element: ImMNElement,
    isShowingFullSource: boolean,
    sessionTransform?: DrawingTransform,
): string | undefined => {
    if (isShowingFullSource) return undefined;

    const cropRect = getImageDrawingCropRect(element);
    if (!cropRect) return undefined;

    const hasSessionTransform =
        !!sessionTransform &&
        (sessionTransform.rotation !== 0 || sessionTransform.scaleX !== 1 || sessionTransform.scaleY !== 1);
    if (!sessionTransform || !hasSessionTransform) return getViewBox(cropRect);

    const drawing = asObject<MNDrawingContent>(getDrawingData(element));
    const canvas = { width: getWidth(drawing), height: getHeight(drawing) };
    if (!canvas.width || !canvas.height) return getViewBox(cropRect);

    const remappedCrop = remapDrawingCrop(
        {
            x: getX(cropRect),
            y: getY(cropRect),
            width: getWidth(cropRect),
            height: getHeight(cropRect),
        },
        canvas,
        sessionTransform,
    );

    return getViewBox(remappedCrop);
};
